import React, { useEffect, useRef, useState } from 'react';
import ModalPortal from '../../../components/ModalPortal';
import { useToolAccess } from '../../../tools/ToolAccessContext';
import { canEditTool } from '../../../tools/catalog';

function toDateInput(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
}

export default function ProjectSettingsModal({
    isOpen,
    onClose,
    project,
    onSave,
    onExport,
    onDelete,
    saving = false
}) {
    const overlayMouseDown = useRef(false);
    const { accessMap, isSuperAdmin } = useToolAccess();
    const canEdit = canEditTool(accessMap, 'Projects', isSuperAdmin);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [allowedOrigins, setAllowedOrigins] = useState('');
    const [expiresAt, setExpiresAt] = useState('');

    useEffect(() => {
        if (!isOpen || !project) return;
        setName(project.name || '');
        setDescription(project.description || '');
        setAllowedOrigins(project.allowedOrigins || '');
        setExpiresAt(toDateInput(project.expiresAt));
    }, [isOpen, project]);

    if (!isOpen || !project) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        onSave({
            name: name.trim(),
            description: description.trim(),
            allowedOrigins: allowedOrigins.trim(),
            expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null
        });
    };

    return (
        <ModalPortal>
        <div
            className="modal-overlay"
            onMouseDown={(e) => { if (e.target === e.currentTarget) overlayMouseDown.current = true; }}
            onMouseUp={(e) => {
                if (e.target === e.currentTarget && overlayMouseDown.current) onClose();
                overlayMouseDown.current = false;
            }}
        >
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ minWidth: '440px', maxWidth: '520px' }}>
                <h2 style={{ color: 'var(--text-primary)', margin: '0 0 0.5rem 0', fontSize: '1.25rem' }}>Project settings</h2>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '1.25rem', fontSize: '0.9rem' }}>
                    Name, description, embed origins, and expiry for this project silo.
                </p>
                <form onSubmit={handleSubmit} className="importer-form">
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>Name</label>
                        <input
                            className="input-field"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            required
                            disabled={!canEdit}
                            autoFocus
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>Description</label>
                        <textarea
                            className="textarea-field"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            disabled={!canEdit}
                            style={{ minHeight: '72px' }}
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>Allowed origins</label>
                        <input
                            className="input-field"
                            value={allowedOrigins}
                            onChange={e => setAllowedOrigins(e.target.value)}
                            disabled={!canEdit}
                            placeholder="e.g. https://app.company.com, http://localhost:3000"
                        />
                        <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0.35rem 0 0 0' }}>
                            Comma-separated origins. Leave empty to inherit deployment defaults.
                        </p>
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>Expires</label>
                        <input
                            type="date"
                            className="input-field"
                            value={expiresAt}
                            onChange={e => setExpiresAt(e.target.value)}
                            disabled={!canEdit}
                        />
                    </div>
                    <div className="modal-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button type="button" className="btn-secondary" onClick={() => onExport(project)}>
                                <i className="fa-solid fa-file-export" style={{ marginRight: '0.35rem' }}></i> Export
                            </button>
                            {canEdit && (
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => onDelete(project)}
                                    style={{ color: 'var(--error)', borderColor: 'var(--error)' }}
                                >
                                    <i className="fa-solid fa-trash-can" style={{ marginRight: '0.35rem' }}></i> Delete
                                </button>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
                            <button
                                type="submit"
                                className="btn-primary"
                                disabled={!canEdit || saving || !name.trim()}
                                style={{ background: 'var(--text-primary)', color: 'var(--bg-primary)' }}
                            >
                                {saving ? 'Saving…' : 'Save'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
        </ModalPortal>
    );
}
